import { gql } from '@apollo/client';

export const USER_INSURANCE_FRAGMENT = gql`
	fragment insuranceCompany on Profile {
		insuranceCompanyTermItem {
			name
			value
			value2
		}
	}
`;

export const NATIONALITY_TERM_ITEM = gql`
	fragment nationalityTermItem on Profile {
		nationalityTermItem {
			id
			name
			value
		}
	}
`;

export const NATIONALITY_OTHER = gql`
	fragment nationalityOther on Profile {
		nationalityOther
	}
`;

export const USER_ADDRESS_FRAGMENT = gql`
	fragment address on Profile {
		address {
			fullAddress
			shortAddress
			formattedFullAddress
			formattedShortAddress
			street
			postCode
			descriptiveNumber
			guideNumber
			city
			province
			street2
			floor
			district
			accommodationZone
			country
			region
			apartmentNumber
		}
	}
`;

export const AVATAR_FRAGMENT = gql`
	fragment avatar on User {
		avatarFile {
			createdAt
		}
	}
`;

export const PROFILE_NAME_FRAGMENT = gql`
	fragment userName on Profile {
		fullName
		firstName
		lastName
	}
`;

export const USER_BADGE = gql`
	fragment badge on User {
		badge {
			title
			theme
		}
	}
`;

export const META = gql`
	fragment meta on User {
		meta {
			key
			value
		}
	}
`;

export const USER_PROFILE_FRAGMENT = gql`
	${USER_ADDRESS_FRAGMENT}
	${PROFILE_NAME_FRAGMENT}
	fragment profile on User {
		profile {
			age
			birthNumber
			formattedBirthNumber
			birthDate
			insuranceNumber
			timezone
			...userName
			...address
		}
	}
`;

export const FULL_PROFILE_FRAGMENT = gql`
	${USER_ADDRESS_FRAGMENT}
	${USER_INSURANCE_FRAGMENT}
	${PROFILE_NAME_FRAGMENT}
	${NATIONALITY_TERM_ITEM}
	${NATIONALITY_OTHER}
	fragment fullProfile on User {
		profile {
			age
			birthNumber
			formattedBirthNumber
			birthDate
			insuranceNumber
			gender
			formattedGender
			timezone
			...nationalityTermItem
			...nationalityOther
			...userName
			...address
			...insuranceCompany
		}
	}
`;

export const HEALTHCARD_FRAGMENT = gql`
	fragment healthCard on User {
		healthCard {
			__typename
			height
			weight
			operations
			job
			sickness
			anamnesisFamily
			allergies
			allergiesOther
		}
		insuranceCardFrontThumbnailUrl
		insuranceCardBackThumbnailUrl
	}
`;

export const USER_INFO = gql`
	${USER_PROFILE_FRAGMENT}
	${AVATAR_FRAGMENT}
	${USER_BADGE}
	query User($userId: UUID!) {
		user(id: $userId) {
			id
			email
			phoneNumber
			formattedPhoneNumber
			isDoctor
			isChild
			...badge
			...avatar
			...profile
		}
	}
`;

export const USER_FULL_INFO = gql`
	${FULL_PROFILE_FRAGMENT}
	${AVATAR_FRAGMENT}
	${PROFILE_NAME_FRAGMENT}
	${USER_BADGE}
	${META}
	query User($userId: UUID!) {
		user(id: $userId) {
			id
			email
			phoneNumber
			formattedPhoneNumber
			isDoctor
			isChild
			partnerIdentifier
			partner
			...meta
			memberOf {
				title
				theme
			}
			parents {
				id
				profile {
					...userName
				}
			}
			csszResult
			vzpResult
			...badge
			...avatar
			...fullProfile
			rootWorkplace {
				name
				address {
					fullAddress
				}
			}
			workplace {
				name
				address {
					fullAddress
				}
			}
			invitations {
				code
				used
			}
		}
	}
`;

export const USER_SIMPLE = gql`
	${AVATAR_FRAGMENT}
	${PROFILE_NAME_FRAGMENT}
	query User($userId: UUID!) {
		user(id: $userId) {
			id
			email
			isDoctor
			isChild
			...avatar
			profile {
				timezone
				...userName
			}
		}
	}
`;

export const USER_RELATIONS = gql`
	${PROFILE_NAME_FRAGMENT}
	query User($userId: UUID!) {
		user(id: $userId) {
			userRelations {
				id
				workplace {
					id
					name
				}
				user {
					id
				}
				relationUser {
					id
					profile {
						...userName
					}
				}
			}
		}
	}
`;

export const USER_VZP_DOCTORS = gql`
	query User($userId: UUID!) {
		user(id: $userId) {
			vzpRegisteredDoctorsResult
		}
	}
`;

export const USER_HEALTH_CARD = gql`
	${HEALTHCARD_FRAGMENT}
	query User($userId: UUID!) {
		user(id: $userId) {
			...healthCard
		}
	}
`;

export const WORKPLACE_SERVICE_SCHEDULE = gql`
	query ServiceSchedules(
		$from: DateTime!
		$to: DateTime!
		$type: ServiceScheduleType
		$workplaceIds: [UUID]
	) {
		serviceSchedules(
			from: $from
			to: $to
			type: $type
			workplaceIds: $workplaceIds
		) {
			data {
				id
				workplaceId
				type
				note
				dateFrom
				dateTo
			}
		}
	}
`;

export const USER_HEALTH_DATA = gql`
	query HealthData(
		$from: DateTime!
		$to: DateTime!
		$patientId: UUID
		$keys: [String]
	) {
		healthData(from: $from, to: $to, userId: $patientId, keys: $keys) {
			data {
				key
				value
				time
				meta {
					key
					value
				}
			}
		}
	}
`;
